import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import {
  CaretLeftIcon,
  CaretRightIcon,
  ClockIcon,
} from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { getWindmillJobsApi } from '../../utils/apis';
import { useDispatch } from 'react-redux';
import {
  setWindmillJobId,
  clearWindmillJobId,
} from '../../utils/redux/slices/windmillFlowSlice';
import WindmillFlowSidebarSkeleton from './WindmillFlowSidebarSkeleton';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../constants/routes';
import NoDataFallback from '../common/NoDataFallback';

interface WindmillFlowSidebarProps {
  flowName: string;
  flowPath?: string;
  selectedJobId?: string | null;
}

const WindmillFlowSidebar: React.FC<WindmillFlowSidebarProps> = ({
  flowName,
  flowPath,
  selectedJobId,
}) => {
  const [page, setPage] = useState(1);
  const jobsPerPage = 10;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch jobs for the current flow
  const {
    data: jobsData,
    isLoading: jobsLoading,
    error: jobsError,
  } = useQuery({
    queryKey: ['windmillJobs', flowName],
    queryFn: () => getWindmillJobsApi(flowName),
    enabled: !!flowName,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });

  const jobsArray =
    jobsData && Array.isArray(jobsData.data) ? jobsData.data : [];
  const totalPages = Math.ceil(jobsArray.length / jobsPerPage);
  const paginatedJobs = jobsArray.slice(
    (page - 1) * jobsPerPage,
    page * jobsPerPage
  );

  useEffect(() => {
    setPage(1);
    dispatch(clearWindmillJobId());
  }, [jobsData, dispatch]);

  const handleJobClick = (job: any) => {
    dispatch(setWindmillJobId(job.id));

    // Navigate directly to job detail page
    if (flowPath) {
      navigate(
        routes.quibbleFlowDetail
          .replace(':flowPath', flowPath)
          .replace(':jobId', job.id)
      );
    }
  };

  const formatStatus = (success: boolean) => {
    return success ? 'Success' : 'Failed';
  };

  const getStatusColor = (success: boolean) => {
    return success ? '#4CAF50' : '#F44336';
  };

  const formatDuration = (duration: number) => {
    return `${(duration / 1000).toFixed(3)}s`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Original sidebar layout for when not used as execution history
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Jobs List */}
      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        {jobsLoading && <WindmillFlowSidebarSkeleton />}
        {jobsError && (
          <Typography color="error">Failed to load executions.</Typography>
        )}
        {paginatedJobs.length > 0
          ? paginatedJobs.map((job: any) => (
              <Box
                key={job.id}
                sx={{
                  mb: 2,
                  p: 2,
                  border:
                    job.id === selectedJobId
                      ? '2px solid #1976d2'
                      : '1px solid #E0E0E0',
                  borderRadius: 2,
                  cursor: 'pointer',
                  backgroundColor:
                    job.id === selectedJobId ? '#e3f2fd' : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      job.id === selectedJobId ? '#e3f2fd' : '#f5f5f5',
                  },
                }}
                onClick={() => handleJobClick(job)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: getStatusColor(job.success),
                    }}
                  >
                    {formatStatus(job.success)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ClockIcon size={12} color="#666" />
                    <Typography variant="caption" color="text.secondary">
                      {job.duration_ms
                        ? formatDuration(job.duration_ms)
                        : 'N/A'}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    backgroundColor: '#f5f5f5',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}
                >
                  ID: {job.id}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {formatDateTime(job.created_at)}
                </Typography>

                {job.args && (
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Args:
                    </Typography>
                    {Object.entries(job.args).map(([key, value]) => (
                      <Typography
                        key={key}
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '10px',
                          color: 'text.secondary',
                          fontFamily: 'monospace',
                        }}
                      >
                        {key}: {String(value)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))
          : !jobsLoading && (
              <NoDataFallback
                message="No executions found for this flow."
                description="There are no executions found for this flow."
              />
            )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
            gap={1}
          >
            <IconButton
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              disabled={page === 1}
              size="small"
            >
              <CaretLeftIcon />
            </IconButton>
            <Typography variant="caption">
              Page {page} of {totalPages}
            </Typography>
            <IconButton
              onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
              disabled={page === totalPages}
              size="small"
            >
              <CaretRightIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WindmillFlowSidebar;
